import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { usePostHog } from '@posthog/react'

type AuthState = {
  checked: boolean
  userUuid: string | null
  email: string | null
  isQualisFlowInternal: boolean | null
}

/**
 * Keeps the PostHog distinct_id in sync with the signed-in user so feature
 * flags can target by user id server-side.
 *
 * - Signed in  -> identify() with the B2C `sub` (state.auth.userUuid).
 * - Signed out -> reset() so the next user starts from a fresh distinct_id.
 *
 * Renders nothing. Must live inside both <PostHogProvider> and the redux
 * <Provider>. Uses usePostHog() so it acts on the same client the
 * feature-flag hooks read from.
 */
export function PostHogIdentify() {
  const posthog = usePostHog()
  const { checked, userUuid, email, isQualisFlowInternal } = useSelector(
    (state: { auth: AuthState }) => state.auth
  )

  useEffect(() => {
    // Wait until the auth check has resolved so we don't reset() the
    // anonymous id on every initial load.
    if (!posthog || !checked) return

    if (userUuid) {
      if (posthog.get_distinct_id() !== userUuid) {
        posthog.identify(userUuid)
      }
    } else {
      posthog.reset()
    }
  }, [posthog, checked, userUuid, email, isQualisFlowInternal])

  return null
}
